import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { SettingsSheet, SheetBlock } from '../settings/sheet';
import AuditLogViewer from '../analytics/AuditLogViewer';
import CancellationsAnalytics from '../analytics/CancellationsAnalytics';
import CustomerAnalytics from '../analytics/CustomerAnalytics';
import DeliveryAnalytics from '../analytics/DeliveryAnalytics';
import DineInAnalytics from '../analytics/DineInAnalytics';
import FinancialAnalytics from '../analytics/FinancialAnalytics';
import InventoryAnalytics from '../analytics/InventoryAnalytics';
import PromotionsAnalytics from '../analytics/PromotionsAnalytics';
import StateFeesAnalytics from '../analytics/StateFeesAnalytics';
import TakeawayAnalytics from '../analytics/TakeawayAnalytics';
import TrainingAnalytics from '../analytics/TrainingAnalytics';
import WasteAnalytics from '../analytics/WasteAnalytics';
import { HomeHeader } from './HomeHeader';
import type { HomeData, PeriodKey } from './homeApi';
import { fetchDineInAnalytics, fetchLegacyAnalytics, fetchTrainingSummary } from './homeApi';
import { groupChannels } from './insights';
import { PremiumLock } from './PremiumLock';
import { ProductsReport } from './ProductsReport';
import { HomeLoadError, SectionsSkeleton } from './HomeSkeleton';

/**
 * Οι «Περισσότερες αναφορές» της Αρχικής: φύλλο με «← Αρχική», τον ίδιο επιλογέα περιόδου και την ίδια
 * «Προσοχή». Τα Προϊόντα είναι νέα σελίδα (δείγμα a_products, από το GET …/home)· οι υπόλοιπες είναι
 * τα υπάρχοντα components/analytics/* με τα δικά τους endpoints. Basic: όλες κλειδωμένες εκτός από το
 * Ιστορικό και την Εκπαίδευση (όπως οι καρτέλες του παλιού ταμπλό).
 */

export type ReportId =
  | 'products' | 'cancellations' | 'tables' | 'takeaway' | 'delivery' | 'customers' | 'promotions'
  | 'stateFees' | 'inventory' | 'waste' | 'vat' | 'audit' | 'training';

export const REPORT_IDS: readonly ReportId[] = [
  'products', 'cancellations', 'tables', 'takeaway', 'delivery', 'customers', 'promotions',
  'stateFees', 'inventory', 'waste', 'vat', 'audit', 'training',
];

/** Χωρίς Premium ανοίγουν μόνο αυτές (όπως «Ιστορικό»/«Εκπαιδευτικές» στο παλιό ταμπλό). */
export const BASIC_REPORTS: readonly ReportId[] = ['audit', 'training'];

export const isReportLocked = (id: ReportId, premium: boolean): boolean => !premium && !BASIC_REPORTS.includes(id);

/** Μία ασύγχρονη φόρτωση με ακύρωση· `key` αλλάζει → ξανά. */
function useLoad<T>(enabled: boolean, key: string, load: (signal?: AbortSignal) => Promise<T>) {
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: boolean }>({ data: null, loading: enabled, error: false });
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    if (!enabled) return undefined;
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    let live = true;
    setState(s => ({ ...s, loading: true, error: false }));
    loadRef.current(ctl?.signal)
      .then(data => { if (live) setState({ data, loading: false, error: false }); })
      .catch(e => { if (live && (e as { name?: string })?.name !== 'AbortError') setState(s => ({ ...s, loading: false, error: true })); });
    return () => {
      live = false;
      ctl?.abort();
    };
  }, [enabled, key, tick]);
  const retry = useCallback(() => setTick(n => n + 1), []);
  return { ...state, retry };
}

/**
 * Μια παλιά αναφορά που «σκάει» (π.χ. απάντηση σε άλλο σχήμα) δεν ρίχνει όλη τη διαχείριση: μήνυμα με
 * «Δοκίμασε ξανά» μέσα στο φύλλο. Νέο `resetKey` (άλλη αναφορά/περίοδος) → ξανά από την αρχή.
 */
class ReportErrorBoundary extends React.Component<{ resetKey: string; children: React.ReactNode }, { failed: boolean; tries: number }> {
  state = { failed: false, tries: 0 };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    console.error('[home] αναφορά:', error);
  }

  componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false });
  }

  render() {
    if (this.state.failed) {
      return <SheetBlock><HomeLoadError onRetry={() => this.setState(s => ({ failed: false, tries: s.tries + 1 }))} /></SheetBlock>;
    }
    return <React.Fragment key={this.state.tries}>{this.props.children}</React.Fragment>;
  }
}

export interface ReportPageProps {
  id: ReportId;
  title: string;
  /** Η περιγραφή της περιόδου («Τρίτη 13 Οκτωβρίου»). */
  periodText: string;
  periodKey: PeriodKey;
  dates: { from: string; to: string };
  /** Επιλογέας περιόδου + Εξαγωγή. */
  tools: React.ReactNode;
  attention?: React.ReactNode;
  home: { data: HomeData | null; current: boolean; loading: boolean; error: Error | null; reload: () => void };
  premium: boolean;
  onBack: () => void;
}

export const ReportPage: React.FC<ReportPageProps> = ({ id, title, periodText, periodKey, dates, tools, attention, home, premium, onBack }) => {
  const { t } = useTranslation(['home', 'common']);
  const { store } = useAuth();
  const storeId = store?.id || 0;
  const locked = isReportLocked(id, premium);
  const { from, to } = dates;
  const range = useMemo(() => ({ from, to }), [from, to]);
  const key = `${storeId}:${from}:${to}`;

  const needsLegacy = !locked && (id === 'inventory' || id === 'vat') && storeId > 0;
  const legacy = useLoad(needsLegacy, key, signal => fetchLegacyAnalytics(storeId, from, to, signal));
  const dineIn = useLoad(!locked && id === 'tables' && storeId > 0, key, signal => fetchDineInAnalytics(storeId, from, to, signal));
  const training = useLoad(!locked && id === 'training' && storeId > 0, key, signal => fetchTrainingSummary(storeId, from, to, signal));

  // Νέα σελίδα: από την κορυφή.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch { /* jsdom */ }
  }, [id]);

  let subtitle = periodText;
  if (id === 'products' && home.data && home.current) {
    const names = groupChannels(home.data.channels).map(g => (
      g.key === 'takeaway' ? t('home:channels.takeaway') : g.key === 'delivery' ? t('home:channels.delivery') : g.key === 'dine_in' ? t('home:channels.dineIn') : (g.label || g.key)
    ));
    if (names.length > 0) subtitle = `${periodText} · ${t('home:productsPage.subtitleChannels', { list: names.join(', ') })}`;
  }

  const legacyBlock = (node: React.ReactNode) => <SheetBlock className="xh-legacy">{node}</SheetBlock>;
  const loadState = (s: { loading: boolean; error: boolean; retry: () => void }) => (s.error
    ? <SheetBlock><HomeLoadError onRetry={s.retry} /></SheetBlock>
    : <SectionsSkeleton count={2} />);

  let body: React.ReactNode;
  if (locked) {
    body = <SheetBlock><PremiumLock /></SheetBlock>;
  } else {
    switch (id) {
      case 'products':
        body = home.data && home.current
          ? <ProductsReport data={home.data} periodKey={periodKey} />
          : home.error ? <SheetBlock><HomeLoadError onRetry={home.reload} /></SheetBlock> : <SectionsSkeleton count={2} />;
        break;
      case 'cancellations':
        body = legacyBlock(<CancellationsAnalytics storeId={storeId} dateFrom={from} dateTo={to} />);
        break;
      case 'tables':
        body = dineIn.data ? legacyBlock(<DineInAnalytics data={dineIn.data} />) : loadState(dineIn);
        break;
      case 'takeaway':
        body = legacyBlock(<TakeawayAnalytics dateRange={range} />);
        break;
      case 'delivery':
        body = legacyBlock(<DeliveryAnalytics dateRange={range} />);
        break;
      case 'customers':
        body = legacyBlock(<CustomerAnalytics dateRange={range} />);
        break;
      case 'promotions':
        body = legacyBlock(<PromotionsAnalytics dateRange={range} />);
        break;
      case 'stateFees':
        body = legacyBlock(<StateFeesAnalytics storeId={storeId} dateFrom={from} dateTo={to} />);
        break;
      case 'inventory':
        body = legacy.data ? (
          <>
            {legacyBlock(<InventoryAnalytics data={legacy.data.inventory} />)}
            {store?.waste_enabled === true && legacyBlock(<WasteAnalytics storeId={storeId} dateFrom={from} dateTo={to} />)}
          </>
        ) : loadState(legacy);
        break;
      case 'waste':
        body = legacyBlock(<WasteAnalytics storeId={storeId} dateFrom={from} dateTo={to} />);
        break;
      case 'vat':
        body = legacy.data ? legacyBlock(<FinancialAnalytics data={legacy.data.financial} />) : loadState(legacy);
        break;
      case 'audit':
        body = legacyBlock(<AuditLogViewer />);
        break;
      case 'training':
        body = legacyBlock(<TrainingAnalytics data={training.data} loading={training.loading} />);
        break;
      default:
        body = null;
    }
  }

  return (
    // Κενός τίτλος: την κεφαλίδα τη γράφει το HomeHeader (ένα h1)· του φύλλου κρύβεται (home.css).
    <SettingsSheet title="" index={id === 'products' && !locked}>
      <HomeHeader
        crumb={(
          <>
            <button type="button" onClick={onBack} data-testid="report-back">
              <ArrowLeft aria-hidden="true" />
              {t('home:reports.back')}
            </button>
            <span className="xh-crumb-sep" aria-hidden="true">›</span>
            <span>{t('home:reports.crumb')}</span>
          </>
        )}
        title={title}
        subtitle={subtitle}
        tools={tools}
      />
      {attention}
      <div data-testid={`report-${id}`}>
        <ReportErrorBoundary resetKey={`${id}:${key}`}>{body}</ReportErrorBoundary>
      </div>
    </SettingsSheet>
  );
};

export default ReportPage;
